import { GridColDef, GridAlignment } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Box, Typography, Link as MuiLink, Button } from '@mui/material';
import { ReportGrid } from '@tap/mui-ui/src/XGridTable/ReportGrid';
import { IComponentCss, getGeneratedDynamicLink, numberHelper } from '@tap/mui-ui';
import { RoutesData } from '../../../routes/routes';
import { Link } from 'react-router';
import { PersonalWorkReportByIssueResult, IApiUserAccount, userService } from '@tap/data-providers';
import clsx from 'clsx';
import * as dateFns from 'date-fns';
import { dateTimeHelper } from '@tap/utils';

/*
*   Персональная выработка по дням для заявок
*/

// Цвета для ячеек со значениями
const css: IComponentCss = {
    tableWrap: {
        position: 'relative',
        height: 'calc(100vh - 165px)',
        width: 1,
        '& .table-wrap.is-value': {
            backgroundColor: '#FBDBB5',
            borderBottomColor: '#FBDBB5',
        },
        '& .table-wrap.is-red-value': {
            backgroundColor: '#FFBDBD',
            borderBottomColor: '#FFBDBD',
        },
        '& .table-wrap.is-weekend': {
            color: '#B65476',
        },
    },
    titleStyle: {
        marginBottom: '15px',
        fontSize: '20px',
    },
    optionsButton: {
        zIndex: 1,
        position: 'absolute',
        right: '4px',
        top: '5px',
        height: '28px',
    },
};

// Проверка, является ли день недели выходным.
const checkIfWeekend = (date: string | number): boolean => {
    const dayOfWeek: number = dateFns.getDay(Number(new Date(date)));

    return (dayOfWeek === 6) || (dayOfWeek === 0) ? true : false;
};

// Столбцы и их параметры для отчета 'Персональная/бригадная выработка по дням'
const getColumns = (reportData: PersonalWorkReportByIssueResult): GridColDef[] => {
    const dates = Object.keys(reportData.stat.days);

    return ([
        {
            // Номер заказа
            field: 'documentKey',
            headerName: 'Заявка',
            type: 'string',
            minWidth: 350,
            flex: 1,
            sortable: false,
            valueGetter: (_val, row) => row.id,
            renderCell: cellParams => {
                if (cellParams.row.isAggRow)
                    return <>Итого</>;


                return (
                    <>
                        <MuiLink
                            component={Link}
                            to={getGeneratedDynamicLink(
                                RoutesData.detailsPageRoutes.path,
                                [{ key: 'documentKey', value: cellParams.row.id }],
                            )}
                        >
                            {cellParams.row.id}
                        </MuiLink>
                        <Typography sx={{ marginLeft: '5px' }}>{cellParams.row.title}</Typography>
                    </>
                );
            },
        },
        ...(dates.map(el => {
            return {
                // Даты
                field: el,
                headerName: dateTimeHelper.format(new Date(el), 'dd MMM, iiiiii', true)?.toString() || '-',
                renderHeader: (params: any) => {
                    return (
                        <div className='MuiDataGrid-columnHeaderTitle'>
                            {(params.colDef.headerName).replace(',', '')}
                        </div>
                    )
                },
                headerAlign: 'right' as GridAlignment,
                align: 'right' as GridAlignment,
                width: 70,
                sortable: false,
                type: 'number' as const,
                valueGetter: (_val: number, row) => numberHelper.formatNumberWithDotDelimiter(row[el] / 60, 2),
                renderCell: (cellParams: any) => cellParams.row[el]
                    ? `${numberHelper.formatNumberWithDotDelimiter(cellParams.row[el] / 60, 2).toString().replace('.', ',')} ч`
                    : '-',
                headerClassName: () =>
                    clsx('table-wrap', {
                        'is-weekend': checkIfWeekend(el),
                    }),
            }
        })),
        {
            // Итого
            field: 'totalCount',
            headerName: 'Итого',
            headerAlign: 'right',
            filterable: false,
            sortable: false,
            align: 'right',
            width: 120,
            valueGetter: (_val, row) => numberHelper.formatNumberWithDotDelimiter(row.totalCount / 60, 2),
            renderCell: cellParams => cellParams.row.totalCount || cellParams.row.totalCount === 0
                ? `${numberHelper.formatNumberWithDotDelimiter(cellParams.row.totalCount / 60, 2).toString().replace('.', ',')} ч`
                : '-',
        },
    ])
}

type PersonalWorkReportByIssueTableProps = {
    executor: IApiUserAccount,
    reportData: PersonalWorkReportByIssueResult,
    onOptionsButtonClick: () => void,
}

const getChartData = (reportData: PersonalWorkReportByIssueResult) => {
    if (!reportData?.rows?.length)
        return {
            rowsList: [],
            pinnedRows: {},
        };

    //TODO : refactor it!
    return {
        rowsList: reportData.rows.map((row) => ({
            id: row?.documentKey,
            totalCount: row.totalSpentMin,
            title: row?.title,
            ...(row.days),
        })),
    }
}

export default function PersonalWorkReportByIssueTable({ executor, reportData, onOptionsButtonClick }: PersonalWorkReportByIssueTableProps) {
    const { rowsList, pinnedRows } = getChartData(reportData);

    return (
        <>
            <Typography sx={css.titleStyle}>Отчёт для исполнителя: {userService.getUserFIO(executor)}</Typography>
            {/* Таблица с графиком */}

            <Box sx={css.tableWrap}>
                <Button
                    size='small'
                    sx={css.optionsButton}
                    onClick={onOptionsButtonClick}
                >
                    Параметры отчёта
                </Button>

                <ReportGrid
                    columns={getColumns(reportData)}
                    rows={rowsList}
                    tableProps={{
                        rowHeight: 22,
                        initialState: {
                            pinnedColumns: {
                                left: ['documentKey'],
                                right: ['totalCount'],
                            },
                        },
                    }}
                    pinnedRows={pinnedRows}
                />
            </Box>
        </>
    );
}
